import { useEffect, useState } from 'react';
import { Alert, App, Col, Form, InputNumber, Modal, Row, Select } from 'antd';
import type { AdminConnector, ConnectorStandard, PowerType } from '@goelectric/shared-types';
import { stationApi } from '../api/stationApi';
import { describeStationApiError } from '../utils/stationErrors';
import { CONNECTOR_STANDARD_OPTIONS, POWER_TYPE_OPTIONS } from '../utils/format';
import {
  buildCreateConnectorBody,
  buildUpdateConnectorBody,
  connectorPowerRule,
  connectorUniqueIdRule,
  type ConnectorFormRow,
} from '../utils/stationForm';

interface ConnectorFormModalProps {
  open: boolean;
  stationId: string;
  /** Potencia del gabinete: techo de la potencia del conector (coherencia). */
  cabinetKw: number;
  /** Conector a editar; `null` para el alta. En edición el número es inmutable. */
  connector: AdminConnector | null;
  /** Números de conector ya usados en el cargador (unicidad del alta). */
  existingConnectorIds: number[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Alta/edición de un conector de un Cargador (US-2.2) en un modal de un solo formulario.
 *
 * Comparte con el editor del alta (`StationConnectorsFields`) las reglas de validación
 * —coherencia de potencia (`connectorPowerRule`) y número único (`connectorUniqueIdRule`)—
 * y las opciones de los selectores. La diferencia es de contrato: en el alta viaja el
 * `id` (`ConnectorInput`); en el PATCH el `id` se muestra deshabilitado y NO viaja (el
 * contrato `updateAdminConnector` no lo acepta, re-sincronizar el core queda para el alta).
 */
export function ConnectorFormModal({
  open,
  stationId,
  cabinetKw,
  connector,
  existingConnectorIds,
  onClose,
  onSaved,
}: ConnectorFormModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<ConnectorFormRow>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEdit = connector !== null;

  // Precarga al abrir (en el alta, el form va vacío).
  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    form.resetFields();
    if (connector) {
      form.setFieldsValue({
        id: connector.id,
        standard: connector.standard,
        powerType: connector.powerType,
        maxPowerKw: connector.maxPowerKw,
        maxVoltageV: connector.maxVoltageV,
        maxAmperageA: connector.maxAmperageA,
      });
    }
  }, [open, connector, form]);

  const handleFinish = async (values: ConnectorFormRow) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && connector) {
        const body = buildUpdateConnectorBody(values);
        if (!body) return;
        await stationApi.updateConnector(stationId, connector.id, body);
        message.success('Conector actualizado.');
      } else {
        const body = buildCreateConnectorBody(values);
        if (!body) return;
        await stationApi.createConnector(stationId, body);
        message.success('Conector agregado.');
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        describeStationApiError(
          caught,
          isEdit ? 'No se pudo actualizar el conector.' : 'No se pudo agregar el conector.',
        ).message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Editar conector' : 'Agregar conector'}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isEdit ? 'Guardar' : 'Agregar'}
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={560}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message={isEdit ? 'No se pudo actualizar el conector' : 'No se pudo agregar el conector'}
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Nº"
              name="id"
              rules={
                isEdit
                  ? undefined
                  : [
                      { required: true, message: 'Ingresa el número del conector.' },
                      { type: 'number', min: 1, message: 'El número debe ser al menos 1.' },
                      connectorUniqueIdRule(() => existingConnectorIds),
                    ]
              }
            >
              <InputNumber
                min={1}
                precision={0}
                disabled={isEdit}
                style={{ width: '100%' }}
                placeholder="1"
                aria-label="Número del conector"
              />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item
              label="Estándar"
              name="standard"
              rules={[{ required: true, message: 'Selecciona el estándar.' }]}
            >
              <Select<ConnectorStandard>
                placeholder="Selecciona el estándar"
                options={CONNECTOR_STANDARD_OPTIONS}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Tipo de potencia" name="powerType">
              <Select<PowerType>
                allowClear
                placeholder="Selecciona el tipo"
                options={POWER_TYPE_OPTIONS}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Potencia (kW)"
              name="maxPowerKw"
              rules={[
                { required: true, message: 'Ingresa la potencia.' },
                connectorPowerRule(() => cabinetKw),
              ]}
            >
              <InputNumber min={0.01} step={1} style={{ width: '100%' }} placeholder="22" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Voltaje (V)" name="maxVoltageV">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="Opcional" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Amperaje (A)" name="maxAmperageA">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="Opcional" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
